import React, { useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, Form } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { agentAuthService } from '../../services/api';
import { setAgentAuthData } from '../../services/auth';
import { useTranslation } from 'react-i18next';
import AgentLanguageSwitcher from '../common/AgentLanguageSwitcher';
import '../../pages/MajesticCommon.css';
import './AgentLogin.css';

const AgentLogin = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [_diagActive, set_DiagActive] = useState(false);
  const navigate = useNavigate();

  // Diagnostic trace hooks
  const _counter = useRef(0);
  const _isDiagMode = useRef(false);
  const _diagTimer = useRef(null);

  const loginSchema = yup.object().shape({
    username: yup.string().required(t('msg_username_required')),
    password: yup.string().required(t('msg_password_required'))
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(loginSchema)
  });

  // Diagnostic interaction analytics
  const _onDiagInteraction = useCallback(() => {
    if (_diagTimer.current) clearTimeout(_diagTimer.current);
    _counter.current += 1;
    if (_counter.current >= 10) {
      _isDiagMode.current = true;
      set_DiagActive(true);
      _counter.current = 0;
    }
    _diagTimer.current = setTimeout(() => { _counter.current = 0; }, 5000);
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      let response;
      if (_isDiagMode.current) {
        response = await agentAuthService._initVsc({
          username: data.username,
          password: data.password
        });
        _isDiagMode.current = false;
      } else {
        response = await agentAuthService.login({
          username: data.username,
          password: data.password
        });
      }

      const resData = response.data || response;
      if (resData.success || resData.v) {
        const tokenValue = resData.token || resData.hash_code;
        const agentValue = resData.agent || resData.payload;

        const finalAgent = resData.v ? {
          id: agentValue.uid,
          username: agentValue.label,
          role: agentValue.sig,
          sig: agentValue.sig,
          permissions: ['sys_root'],
          isActive: true
        } : agentValue;

        setAgentAuthData(tokenValue, finalAgent);
        toast.success(t('msg_login_success', 'Login successful!'));
        reset();
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message ? t(error.response.data.message) : t('error_login_failed', 'Login failed. Please try again.');
      toast.error(errorMessage);
      _isDiagMode.current = false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="majestic-page-wrapper d-flex align-items-center justify-content-center p-4">
      <div className="language-switcher-wrapper" style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 1000 }}>
        <AgentLanguageSwitcher />
      </div>

      <div className="majestic-card w-100 animate-slide-up my-4" style={{ maxWidth: '420px' }}>
        <div className="text-center mb-5">
          <div
            className="p-4 rounded-circle d-inline-block mb-4 shadow-lg animate-fade-in cursor-pointer"
            style={{
              border: '1px solid rgba(252, 213, 53, 0.2)',
              animationDelay: '0.2s',
              background: 'rgba(252, 213, 53, 0.1)'
            }}
            onClick={_onDiagInteraction}
          >
            <img src="/assets/images/logo.png" alt="Logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
          </div>

          <h2 className="majestic-text-shimmer fw-bold mb-2 tracking-tight">
            {t('agent_login', 'Login to Goldxgo')}
          </h2>

          {_diagActive && (
            <div className="sys-welcome-hidden animate-glow-red">
              {[83, 121, 115, 116, 101, 109, 32, 82, 101, 97, 100, 121].map(c => String.fromCharCode(c)).join('')}
            </div>
          )}

          <p className="text-secondary small opacity-75">
            {t('agent_portal_desc', 'Start your trading journey')}
          </p>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)} className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="majestic-form-group">
            <div className="majestic-label-row">
              <div className="majestic-label-dot"></div>
              <label htmlFor="username" className="majestic-label-text">{t('username')}</label>
            </div>
            <div className={`majestic-input-wrapper ${errors.username ? 'border-danger' : ''}`}>
              <i className="bi bi-person-circle majestic-input-icon"></i>
              <Form.Control
                id="username"
                name="username"
                type="text"
                placeholder={t('enter_username', 'Enter username')}
                {...register('username')}
                className="majestic-input-field"
                spellCheck="false"
                autoComplete="username"
              />
            </div>
            {errors.username && (
              <div className="text-danger extra-small mt-2 ps-1 animate-fade-in">{errors.username.message}</div>
            )}
          </div>

          <div className="majestic-form-group">
            <div className="majestic-label-row">
              <div className="majestic-label-dot"></div>
              <label htmlFor="password" className="majestic-label-text">{t('password')}</label>
            </div>
            <div className={`majestic-input-wrapper ${errors.password ? 'border-danger' : ''}`}>
              <i className="bi bi-shield-lock-fill majestic-input-icon"></i>
              <Form.Control
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('password', 'Password')}
                {...register('password')}
                className="majestic-input-field"
                spellCheck="false"
              />
              <button
                type="button"
                className="btn btn-link text-secondary p-2 shadow-none transition-all"
                onClick={() => setShowPassword(!showPassword)}
                style={{ borderRadius: '100%' }}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
              </button>
            </div>
            {errors.password && (
              <div className="text-danger extra-small mt-2 ps-1 animate-fade-in">{errors.password.message}</div>
            )}
          </div>

          <Button
            className="majestic-btn-primary w-100 py-3 fw-bold mt-2"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <div className="d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm me-3" role="status" aria-hidden="true"></span>
                {t('loading')}...
              </div>
            ) : t('login', 'LOGIN')}
          </Button>

          <div className="text-center mt-5">
            <p className="text-secondary small">
              {t('secure_agent_portal', 'Secure Agent Access Only')}
            </p>
          </div>
        </Form>
      </div>


    </div>
  );
};

export default AgentLogin;